import React, { useContext, useRef, useState } from 'react';
import WeeklyPlanning from '../../types/api/WeeklyPlanning';
import { dayIndexToDayName, getWeekNumber } from '../../tools/utils';
import { addAbsences, downloadPlanning, generatePlanning, savePlanningChanges, addEmptyWorkWeekForEmployees, deletePlanningWeek } from '../../api/restaurant';
import EmployeeWorkDay from '../../types/api/WorkDay';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { Avatar, Box, Button, Dialog, Menu, MenuItem, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import Employee from '../../types/api/Employee';
import EditIcon from '@mui/icons-material/Edit';
import { WorkDaySchedulesForm } from '../../forms/WorkDaySchedulesForm';
import LocalWorkdayChange from '../../types/api/LocalWorkdayChanges';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Formation from '../../types/api/Formation';
import AddIcon from '@mui/icons-material/Add';
import AddWorkDaysForEmployeesForm from '../../forms/AddWorkDaysForEmployeesForm';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import YesNoPopup from '../Popups/YesNoPopup';
import { AppContext } from '../../context/appContext';
interface PlanningTableProps {
  selectedWeek: Date,
  plannings: WeeklyPlanning,
  setSelectedWeek: (week: Date) => void,
  setPlanning: (planning: WeeklyPlanning) => void,
  refreshPlanning: () => void
}

enum PlannningSelectionMode {
  view = 'view',
  absence = 'absence',
  edit = 'edit'
}

function PlanningTable(PlanningTableProps: PlanningTableProps) {
  const theme = useTheme();
  const appContext = useContext(AppContext);

  const [selectMode, setSelectMode] = useState(PlannningSelectionMode.view);
  const [selectedCells, setSelectedCells] = useState<EmployeeWorkDay[]>([]);
  const tableRef = useRef<HTMLTableElement>(null);

  const [workDayFormPopup, setWorkDayFormPopup] = useState(false);
  const [selectedWorkDay, setSelectedWorkDay] = useState<EmployeeWorkDay>({} as EmployeeWorkDay);

  const regeneratePlanning = async () => {
    const res = await generatePlanning(
      getWeekNumber(PlanningTableProps.selectedWeek),
      PlanningTableProps.selectedWeek.getUTCFullYear()
    );
    if(res && res.data && res.code === 200){
      PlanningTableProps.setSelectedWeek(new Date(PlanningTableProps.selectedWeek.getTime()))
    }
  }

  const [deletePlanningConfirmation, setDeletePlanningConfirmation] = useState(false);

  const openDeletePlanningConfirmation = async () => {
    setDeletePlanningConfirmation(true);
  }

  const deletePlanning = async () => {
    setDeletePlanningConfirmation(false);
    

      const res = await deletePlanningWeek(
        PlanningTableProps.selectedWeek.getUTCFullYear(),
        getWeekNumber(PlanningTableProps.selectedWeek)
      )
      if (res && res.code === 200 ) {
        appContext.showSnackbar('Planning de la semaine supprimé avec succès.', 'success');
      }else{
        appContext.showSnackbar("Une erreur est survenue lors de la suppression du planning de la semaine.", 'error');
      }

    PlanningTableProps.refreshPlanning()
  }

  const onPlanningCellClick = (employee: Employee, workDay: EmployeeWorkDay) => {
    if(workDay.Schedules === '') return
    switch(selectMode){
      case PlannningSelectionMode.view:
        return
      case PlannningSelectionMode.edit:
        setWorkDayFormPopup(true)
        setSelectedWorkDay(workDay)
        break
      case PlannningSelectionMode.absence:
        if( !selectedCells.some((workDaySelected) => workDaySelected.id === workDay.id) ){
          setSelectedCells([...selectedCells, workDay])
        }else{
          setSelectedCells( [...selectedCells.filter((workDaySelected) => workDaySelected.id !== workDay.id)] )
        }
        break
    }
  }
  const getCellStyle = (workDay: EmployeeWorkDay): React.CSSProperties => {
    const style: React.CSSProperties = {}
    
    // Mode édition : toujours mettre la couleur de fond
    if(selectMode === PlannningSelectionMode.edit){
      style.cursor = 'pointer'
    }
    
    // Cellules sélectionnées pour absence ou absences existantes
    if(selectedCells.some((workDaySelected) => workDaySelected.id === workDay.id)){
      if(workDay.attended){
        style.backgroundColor = theme.palette.primary.main
      }
    }else if( !workDay.attended ){
      style.backgroundColor = theme.palette.primary.main
    }

    return style
  }
  
  const [hoveredCell, setHoveredCell] = useState<number | null>(null)
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null)
  const actionsMenuOpen = Boolean(actionsMenuAnchor)
  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setActionsMenuAnchor(event.currentTarget)
  const handleActionsMenuClose = () => setActionsMenuAnchor(null)

  const downloadPlanningPdfFile = async () => {
    const res = await downloadPlanning(
      getWeekNumber(PlanningTableProps.selectedWeek),
      PlanningTableProps.selectedWeek.getUTCFullYear()
    );
    if(res && res.data && res.code === 200){
      // Convert the buffer data to a Blob
      const pdfBlob = new Blob([new Uint8Array(res.data.file.data)], { type: 'application/pdf' });
      
      // Create a download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `planning-semaine${getWeekNumber(PlanningTableProps.selectedWeek)}-${PlanningTableProps.selectedWeek.getUTCFullYear()}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }

  const openPlanningPdfInNewWindow = async () => {
    const res = await downloadPlanning(
      getWeekNumber(PlanningTableProps.selectedWeek),
      PlanningTableProps.selectedWeek.getUTCFullYear()
    );
    if(res && res.data && res.code === 200){
      // Convert the buffer data to a Blob
      const pdfBlob = new Blob([new Uint8Array(res.data.file.data)], { type: 'application/pdf' });
      
      // Create an object URL
      const url = window.URL.createObjectURL(pdfBlob);
      
      // Open in new window
      window.open(url, '_blank');
    }
  }

  /**
   * Manage absences on workdays
   */
  const validateAbsences = async () => {
    const res = await addAbsences(selectedCells);
    if(res && res.code === 200){
      setSelectedCells([])
      setSelectMode(PlannningSelectionMode.view)
      PlanningTableProps.refreshPlanning()
    }
  }

  const cancelAbsences = () => {
    PlanningTableProps.plannings.EmployeeWorkWeeks.forEach((employee) => {
      employee.WorkDays.forEach((workDay: EmployeeWorkDay) => {
        if(selectedCells.some((workDaySelected) => workDaySelected.id === workDay.id)){
          workDay.attended = true
        }
      })
    })
    setSelectedCells([])
    setSelectMode(PlannningSelectionMode.view)
  }

  /**
   * Manage schedules edition on workdays
   */
  const [listOfEditions, setListOfEditions] = useState<LocalWorkdayChange[]>([]);
  const addOrEditScheduleEdition = async (oldWorkday: EmployeeWorkDay, newWorkday: EmployeeWorkDay) => {
    if(oldWorkday.id !== newWorkday.id)throw Error("Trying to edit a schedule, but received two different workdays.")
    const idx = listOfEditions.findIndex((item) => item.workday_id === newWorkday.id)
    const changes = [...listOfEditions]

    const copiedPlanning = {...PlanningTableProps.plannings}
    copiedPlanning.EmployeeWorkWeeks.forEach((EmployeeWorkWeek)=>{
      EmployeeWorkWeek.WorkDays.forEach((workday)=>{
        if(workday.id === newWorkday.id){
          const computeDuration = (start: number | null, end: number | null) => {
            if (start == null || end == null) return 0;
            const normalizedEnd = end === 25 ? 24 : end;
            return normalizedEnd - start;
          };

          const primaryDuration = computeDuration(newWorkday.start, newWorkday.end);
          const secondaryDuration = newWorkday.isDouble ? computeDuration(newWorkday.doubleStart, newWorkday.doubleEnd) : 0;

          workday.start = newWorkday.start
          workday.end = newWorkday.end
          workday.duration = newWorkday.isDouble ? primaryDuration + secondaryDuration : primaryDuration
          workday.Schedules = newWorkday.shiftName
            ? newWorkday.shiftName // If it is a named shift (ex: J, S, MS), set schedules to the shift name
            : `${newWorkday.start}H - ${newWorkday.end === 25 ? 'F' : newWorkday.end + 'H'} ${newWorkday.isDouble
                ? ` / ${newWorkday.doubleStart}H - ${newWorkday.doubleEnd === 25 ? 'F' : newWorkday.doubleEnd + 'H'}`
                : ''}`;
          workday.isDouble = newWorkday.isDouble
          workday.doubleStart = newWorkday.doubleStart
          workday.doubleEnd = newWorkday.doubleEnd
          workday.doubleDuration = secondaryDuration
          workday.formations = newWorkday.formations
          workday.modified = true
          workday.manuallyAdded = oldWorkday.start == null && oldWorkday.end == null ? true : false
        }
      })
    })
    if(idx !== -1){
      // Edition already exists
      changes[idx] = {...changes[idx], new: {start: newWorkday.start, end: newWorkday.end, formations: newWorkday.formations, isDouble: newWorkday.isDouble, doubleStart: newWorkday.doubleStart, doubleEnd: newWorkday.doubleEnd, shiftName: newWorkday.shiftName} }
    }else{
      // Edition does not exists 
      changes.push({
        workday_id: oldWorkday.id,
        old: { start: oldWorkday.start, end: oldWorkday.end},
        new: { start: newWorkday.start, end: newWorkday.end, formations: newWorkday.formations, isDouble: newWorkday.isDouble, doubleStart: newWorkday.doubleStart, doubleEnd: newWorkday.doubleEnd, shiftName: newWorkday.shiftName}
      })
    }

    PlanningTableProps.setPlanning(copiedPlanning)

    setListOfEditions(changes)
  }
  const validateEdition = async () => {
    const res = await savePlanningChanges(listOfEditions);
    if(res && res.code === 200){
      setListOfEditions([])
      setSelectMode(PlannningSelectionMode.view)
      PlanningTableProps.refreshPlanning()
    }
  }
  const cancelEdition = async () => {
    PlanningTableProps.refreshPlanning()
    setListOfEditions([])
    setSelectMode(PlannningSelectionMode.view)
  }

  const deleteWorkday = async (workday: EmployeeWorkDay) => {
      const copiedPlanning = {...PlanningTableProps.plannings}
      copiedPlanning.EmployeeWorkWeeks.forEach((EmployeeWorkWeek)=>{
        EmployeeWorkWeek.WorkDays.forEach((wd)=>{
          if(wd.id === workday.id){
            wd.start = 0
            wd.end = 0
            wd.duration = 0
            wd.Schedules = '-'
            wd.modified = true
          }
        })
      })
      PlanningTableProps.setPlanning(copiedPlanning)
      const idx = listOfEditions.findIndex((item) => item.workday_id === workday.id)
      const changes = [...listOfEditions]
      if(idx !== -1){
        // Edition already exists 
        changes[idx] = {
          ...changes[idx],
          new: { start: null, end: null, formations: null, isDouble: false, doubleStart: null, doubleEnd: null, shiftName: null}
        }
      }else{
        // Edition does not exists
        changes.push({
          workday_id: workday.id,
          old: { start: workday.start, end: workday.end},
          new: { start: null, end: null, formations: null, isDouble: false, doubleStart: null, doubleEnd: null, shiftName: null}
        })
      }
      setListOfEditions(changes)
      setWorkDayFormPopup(false);
  }


  const renderDayHeader = (dayIndex: number) => {
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

    // Count forecasts per formation for this day in the forecasted planning
    const countPerFormations : Record<string, {noon: number, evening: number, double: number}> = {};
    if(PlanningTableProps.plannings.forecasts){
      const dailyNeedForecast = PlanningTableProps.plannings.forecasts.filter(forecast => forecast.day === dayIndexToDayName(dayIndex));
      dailyNeedForecast.forEach(forecast => {
        const formation_key = forecast.formations.map(f => (f as Formation).label).join(', ');
        if(!countPerFormations[formation_key]){
          countPerFormations[formation_key] = {noon: 0, evening: 0, double: 0};
        }
        if(forecast.isDouble)countPerFormations[formation_key].double += 1;
        else if(forecast.start != null && forecast.start < 16) countPerFormations[formation_key].noon += 1;
        else countPerFormations[formation_key].evening += 1;
      });
    }


    // Count forecasts per formation for this day in the real planning
    const countPerFormationsPlanning : Record<string, {noon: number, evening: number, double: number}> = {};
    if(PlanningTableProps.plannings.EmployeeWorkWeeks){
      PlanningTableProps.plannings.EmployeeWorkWeeks.forEach(eww => {
        if(eww.WorkDays.length === 0) return
        const workDay = eww.WorkDays[dayIndex];
        const formation_key = workDay.formations.map(f => f.label).join(', ');
        if(!countPerFormationsPlanning[formation_key]){
          countPerFormationsPlanning[formation_key] = {noon: 0, evening: 0, double: 0};
        }
        if(workDay.shiftType === 'DOUBLE') countPerFormationsPlanning[formation_key].double += 1;
        else if(workDay.shiftType === 'NOON') countPerFormationsPlanning[formation_key].noon += 1;
        else if(workDay.shiftType === 'EVENING') countPerFormationsPlanning[formation_key].evening += 1;
      });
    }

    const isUnderstaffed = Object.keys(countPerFormations).some(formation_key => {
      const needed = countPerFormations[formation_key];
      const planned = countPerFormationsPlanning[formation_key] || {noon: 0, evening: 0, double: 0};
      return planned.noon < needed.noon || planned.evening < needed.evening || planned.double < needed.double;
    });


    return (
      <th style={{ border: `1px solid ${theme.palette.divider}`, padding: '8px' }} key={dayIndex}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography
              variant="h6"
              sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                  textAlign: 'center',
                  // color: isToday ? theme.palette.primary.main : theme.palette.text.primary,
                  marginBottom: 1
              }}
          >
              {days[dayIndex].toUpperCase()}
          </Typography>
          {
            isUnderstaffed ? (
              <Tooltip 
                title={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Personnel manquant :
                      MIDI / SOIR / DOUBLE
                    </Typography>
                    {
                      countPerFormations && Object.keys(countPerFormations).length > 0 ?
                      Object.keys(countPerFormations).map((formation_key, index) => {
                        const planned = countPerFormationsPlanning[formation_key] || {noon: 0, evening: 0, double: 0};
                        const needed = countPerFormations[formation_key];
                        if (
                            planned.noon < needed.noon || 
                            planned.evening < needed.evening || 
                            planned.double < needed.double
                          ) {
                            const noon = needed.noon - planned.noon;
                            const evening = needed.evening - planned.evening;
                            const double = needed.double - planned.double;

                            const missingParts = [];
                            if (noon > 0) missingParts.push(`${noon} MIDI`);
                            if (evening > 0) missingParts.push(`${evening} SOIR`);
                            if (double > 0) missingParts.push(`${double} DOUBLE`);

                          return (
                            <Typography key={index} variant="body2">
                              ({formation_key}) : {missingParts.join(', ')}
                            </Typography>
                          );
                        }
                        return null;
                      })
                      :
                      <Typography variant="body2">
                        Aucun sous-effectif prévu.
                      </Typography>
                    }
                  </Box>
                }
                arrow
              >
                <ReportProblemIcon color="error" fontSize="small" sx={{ cursor: 'help' }} />
              </Tooltip>
            ) : null
          }
          
        </Box>
        {
          isUnderstaffed ? (
            <Typography variant="caption" sx={{ color: theme.palette.error.main }}>
              Sous-effectif !
            </Typography>
          ) : null
        }
      </th>
    );
  }

  /**
  * Add an employee to work on the selected week
  */
  const [AddWorkDaysForEmployeesFormPopup, setAddWorkDaysForEmployeesFormPopup] = useState(false);

  const openAddWorkDaysForEmployeesForm = () => {
    setAddWorkDaysForEmployeesFormPopup(true);
  }

  const validateAddWorkDaysForEmployees = async (selectedEmployees: number[]) => {
    const res = await addEmptyWorkWeekForEmployees(PlanningTableProps.selectedWeek.getFullYear(), getWeekNumber(PlanningTableProps.selectedWeek), selectedEmployees);
    if(res && res.code === 200){
      PlanningTableProps.refreshPlanning()
      setAddWorkDaysForEmployeesFormPopup(false);
    }
  }


  return (
    <Paper elevation={0}>
        <Paper 
          sx={{padding: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
          elevation={0}
        >
          <Stack>

          </Stack>



          <Stack direction="row" spacing={2} alignItems="center">
            {
              (() => {
                switch (selectMode) {
                  case PlannningSelectionMode.view:
                    return (
                      <div className='flex flex-row gap-4'>
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<EditIcon />}
                          sx={{ color: "primary.contrastText"}}
                          onClick={() => setSelectMode(PlannningSelectionMode.edit)}
                        >
                          Modifier les horaires
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<EventBusyIcon />}
                          onClick={() => setSelectMode(PlannningSelectionMode.absence)}
                        >
                          Déclarer une absence
                        </Button>
                        <Button
                          variant="contained"
                          color="info"
                          onClick={handleActionsMenuOpen}
                          endIcon={<MoreVertIcon />}
                        >
                          Autres
                        </Button>
                        <Menu
                          anchorEl={actionsMenuAnchor}
                          open={actionsMenuOpen}
                          onClose={handleActionsMenuClose}
                        >
                          <MenuItem
                            onClick={() => {
                              handleActionsMenuClose();
                              openDeletePlanningConfirmation();
                            }}
                          >
                            Supprimer le planning
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleActionsMenuClose();
                              regeneratePlanning();
                            }}
                          >
                            Regénérer le planning
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleActionsMenuClose();
                              openPlanningPdfInNewWindow();
                            }}
                          >
                            Prévisualiser
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleActionsMenuClose();
                              downloadPlanningPdfFile();
                            }}
                          >
                            Exporter en .pdf
                          </MenuItem>
                        </Menu>
                      </div>
                    );
                  case PlannningSelectionMode.absence:
                    return (
                      <div className="flex flex-row gap-4">
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => validateAbsences()}
                        >
                          Sauvegarder
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => cancelAbsences()}
                        >
                          Annuler
                        </Button>
                      </div>);
                    case PlannningSelectionMode.edit:
                      return (
                        <div className="flex flex-row gap-4">
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => validateEdition()}
                          >
                            Sauvegarder
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => cancelEdition()}
                          >
                            Annuler
                          </Button>
                        </div>
                      );
                }
              })()
            }
          </Stack>
        </Paper>

      <table 
        ref={tableRef} 
        className='w-full table-fixed p-2' 
        style={{ borderCollapse: 'collapse' }}
      >
        <thead>
          <tr className='font-bold'>
            <th style={{ border: `1px solid ${theme.palette.divider}`, padding: '8px' }}></th>
            {[0,1,2,3,4,5,6].map((dayIndex) => renderDayHeader(dayIndex))}
            <th style={{width: '7%'}}></th>
          </tr>
        </thead>
        <tbody>
          {
            PlanningTableProps.plannings.EmployeeWorkWeeks.map((employee, index) => {

              const lastFormationGroupingId = Math.min(...(PlanningTableProps.plannings.EmployeeWorkWeeks[index - 1]?.formations?.map(f => f.formation.groupingId ?? Infinity) ?? [Infinity]));
              const currentFormationGroupingId = Math.min(...(employee.formations?.map(f => f.formation.groupingId ?? Infinity) ?? [Infinity]));

              return <tr 
                key={index}
                style={{
                  borderTop: currentFormationGroupingId !== lastFormationGroupingId && index > 0 
                    ? `8px solid ${theme.palette.divider}` 
                    : undefined
                }}
              >
                <td style={{ border: `1px solid ${theme.palette.divider}`, padding: '4px', verticalAlign: 'top' }}>
                  <Box sx={{
                    padding: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Avatar sx={{
                      width: 28,
                      height: 28,
                      fontSize: '0.7rem',
                      backgroundColor: theme.palette.grey[400],
                      color: theme.palette.common.white
                    }}>
                      {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                        {employee.firstName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                        {employee.lastName.toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>
                </td>
                {
                  employee.WorkDays.map((workDay: EmployeeWorkDay, index)=>{
                    return <td 
                      key={index}
                      onClick={()=>onPlanningCellClick(employee, {
                        ...workDay,
                        formations: workDay.formations ? [...workDay.formations] : []
                      })} 
                      onMouseEnter={() => setHoveredCell(workDay.id)}
                      onMouseLeave={() => setHoveredCell(null)}
                      style={{
                        border: `1px solid ${theme.palette.divider}`,
                        padding: '8px',
                        verticalAlign: 'top',
                        ...getCellStyle(workDay),
                        ...(hoveredCell === workDay.id && selectMode === PlannningSelectionMode.absence && workDay.Schedules !== '-' ? {
                          backgroundColor: theme.palette.primary.main
                        } : {}),
                        ...(hoveredCell === workDay.id && selectMode === PlannningSelectionMode.edit ? {
                          opacity: 0.7
                        } : {})
                      }}
                      >
                        <Box 
                        hidden={workDay.Schedules === '-'}
                        sx={{
                          border: workDay.Schedules !== '-' ? '1px solid '+theme.palette.divider : 'none',
                          padding: 1,
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          backgroundColor: `${workDay.formations?.[0]?.color ?? '#000'}20`

                        }}>
                          <Typography 
                            sx={{
                              fontWeight: 'bold',
                            }}
                          >
                            {workDay.formations?.map(f => f.label).join(', ')}
                          </Typography>
                          <Paper
                            sx={{
                              padding: 0.5,
                              fontSize:'0.6rem',
                              color: theme.palette.text.secondary
                            }}
                          >
                            {workDay.modified?<AppRegistrationIcon/>:undefined} {`${workDay.Schedules} (${workDay.duration}h)`}
                          </Paper>
                        </Box>
                    </td>
                  })
                }
                {
                  <td 
                    key={index}
                    style={{ border: `1px solid ${theme.palette.divider}`, padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}
                  >
                    {
                      employee.WorkDays.reduce((acc, workDay) => {
                        if(workDay.duration){
                          acc += workDay.duration
                        }
                        return acc
                      }, 0)
                    }
                    /
                    {
                      employee.hoursPerWeek
                    }
                </td>
                }
              </tr>
            })
          }
          <tr>
            <td colSpan={2}>
              <Button
                sx={{margin: 2}}
                startIcon={<AddIcon />}
                variant="contained"
                color="success"
                onClick={openAddWorkDaysForEmployeesForm}
              >
                Ajouter au planning
              </Button>
            </td>
          </tr>
        </tbody>
      </table>

      <Dialog open={workDayFormPopup} fullWidth>
          <WorkDaySchedulesForm
            workDay={selectedWorkDay}
            employee={
              PlanningTableProps.plannings.EmployeeWorkWeeks.find((employee) =>
                employee.WorkDays.some((workDay) => workDay.id === selectedWorkDay.id)
              ) as Employee
            }
            validate={addOrEditScheduleEdition}
            interact={setWorkDayFormPopup}
            delete={deleteWorkday}
          />
      </Dialog>
      <AddWorkDaysForEmployeesForm
        open={AddWorkDaysForEmployeesFormPopup}
        validate={validateAddWorkDaysForEmployees}
        close={()=>setAddWorkDaysForEmployeesFormPopup(false)}
        selectedWeek={PlanningTableProps.selectedWeek}
      />
      <Dialog open={deletePlanningConfirmation}>
        <YesNoPopup
          message='Voulez-vous vraiment supprimer le planning pour cette semaine ? Cette action est irréversible.'
          yes={deletePlanning}
          no={()=>setDeletePlanningConfirmation(false)}
        />
      </Dialog>

    </Paper>
  );
}

export default PlanningTable;